<script setup lang="ts">
import { Credentials } from '@/authentication/types';
import Button from '@/components/buttons/Button.vue';
import DateTimeFormatter from '@/components/formatters/DateTimeFormatter.vue';
import VeePasswordField from '@/components/forms/PasswordField/VeePasswordField.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import { warningSign } from '@/components/icons/icons';
import SimplePage from '@/components/pages/SimplePage/SimplePage.vue';
import { routenames } from '@/router/routenames';
import { useAuthenticationStore } from '@/stores/authenticationStore';
import { useSystemStore } from '@/stores/systemStore.ts';
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const router = useRouter();
const authenticationStore = useAuthenticationStore(router);
const systemStore = useSystemStore();
const route = useRoute();

const fromRoute = router.resolve({
  path: String(route.query.from),
});

const comingFromActivation =
  fromRoute.name === routenames.activation && route.query.dontShowGreetings === undefined;
const comingFromResetPassword = fromRoute.name === routenames.resetPassword;

const {
  form: { handleSubmit, values, isSubmitting },
} = useVeeForm<Credentials>({
  confirmationOptions: {
    skipDirtyCheck: 'always', // "dirty"-dialog not needed here
  },
  mode: 'edit',
});

const signInError = ref(false);

const submit = handleSubmit(async (credentials) => {
  signInError.value = !(await authenticationStore.signIn(credentials));
});
</script>

<template>
  <SimplePage
    :headline="
      $t(
        comingFromActivation
          ? 'Congratulations'
          : comingFromResetPassword
            ? 'PasswordUpdated'
            : 'SignIn',
      )
    "
  >
    <p v-if="comingFromActivation" class="mb-6 text-pre-line">
      {{ $t('AccountSuccessfullyActivatedMsg') }}
    </p>
    <p v-else-if="comingFromResetPassword" class="mb-6 text-pre-line">
      {{ $t('PasswordHasBeenResetSuccessfullyMsg') }}
    </p>
    <v-alert v-if="signInError" color="error" class="mb-6">
      {{ $t('SignInErrorMsg') }}
    </v-alert>

    <v-alert
      v-if="systemStore.maintenanceModeInfo"
      type="warning"
      :title="$t('Exceptions.MaintenanceModeIsEnabledMsg')"
      :text="$t('MaintenanceModeIsEnabledMsg')"
      :icon="warningSign"
      class="mb-6 text-left"
    />

    <v-alert
      v-if="authenticationStore.session && !authenticationStore.session.isLicenseValid"
      type="warning"
      :title="$t('LicenseHasExpired')"
      :icon="warningSign"
      class="mb-6 text-left"
    >
      <p>{{ $t('LicenseHasExpiredMsg') }}</p>
      <p>
        {{ $t('ExpirationDate') }}:
        <DateTimeFormatter :value="authenticationStore.session?.licenseExpirationDate" />
      </p>
      <p class="mb-2">{{ $t('LicenseHasExpiredRemedy') }}</p>
    </v-alert>

    <v-form class="form" :disabled="isSubmitting" @submit="submit">
      <VeeTextField
        class="gutterTop"
        name="emailAddress"
        type="email"
        :label="$t('Email')"
        autocomplete="username"
      />
      <VeePasswordField
        class="gutterTop"
        name="password"
        :label="$t('Password')"
        autocomplete="current-password"
      />
      <div class="text-right">
        <Button
          class="mt-2"
          variant="ghost"
          size="x-small"
          :label="$t('ForgotPassword')"
          :to="{
            name: routenames.forgotPassword,
            query: { email: values.emailAddress },
          }"
        />
      </div>
      <VeeSubmitButton :text="$t('SignIn')" class="gutterTop" :block="true" />
    </v-form>
  </SimplePage>
</template>

<style lang="scss" scoped>
@use '@/styles/variables';

.form {
  margin: 0 auto;
  max-width: variables.$maxSimpleLayoutFormWidth;
  text-align: left;
}
</style>
